import { useCallback, useEffect, useRef, useState } from "react";
import type { AppState } from "@juegossc/shared";
import { appState } from "../lib/api";
import { applyTheme } from "../lib/theme";
import { setSoundUrls, setSoundEnabled } from "./../lib/audio";
import { applyFonts } from "./../lib/fonts";

// Polling cada N segundos para detectar cambios de admin sin requerir
// recarga manual (modo prueba, premios activados/desactivados, etc.).
// 30s es buen balance: detecta cambios rápido sin cargar el servidor.
const POLL_INTERVAL_MS = 30_000;

export function useAppState(enabled = true) {
  const [state, setState] = useState<AppState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const timerRef = useRef<number | null>(null);

  const refetch = useCallback(async () => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    setError(null);
    try {
      const data = await appState();
      setState(data);
      // Aplicar tema en cuanto llega el state.
      applyTheme({
        color_primary: data.global.color_primary,
        color_accent: data.global.color_accent,
      });
      // Registrar URLs de sounds cargados (migration 017). Internamente
      // dispara precarga async de cada uno. Idempotente: las URLs ya
      // cacheadas no se vuelven a fetchear.
      setSoundUrls(data.sounds ?? {});
      // Migration 029: aplicar el toggle de sonido por sucursal. Sin
      // esto, todos los wrappers de audio.ts suenan aunque el admin
      // haya desactivado el switch en Mi pantalla (/admin/pantalla).
      setSoundEnabled(data.settings.sound_enabled);
      // Aplicar tipografías (migration 018). applyFonts inyecta los
      // <link> a Google Fonts necesarios y setea las CSS vars
      // --font-heading / --font-body / --font-display.
      applyFonts({
        heading: data.global.font_heading,
        body: data.global.font_body,
        display: data.global.font_display,
      });
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    // Fetch inicial + polling.
    refetch();
    if (!enabled) return;

    timerRef.current = window.setInterval(() => {
      // Polling silencioso — no setea loading=true para no flickear UI.
      refetch();
    }, POLL_INTERVAL_MS);

    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [refetch, enabled]);

  return { state, loading, error, refetch };
}
